import { Fragment, useEffect, useState } from "react";
import { Settings } from "react-feather";
import { Button, Card, CardBody, Col, Container, Form, FormGroup, Input, Label, Row } from "reactstrap";
import { baseURL } from "../../../../Services/api/baseURL";
import axios from "axios";
import { useFormik } from "formik";
import * as Yup from 'yup'
import Swal from "sweetalert2";
import Loader from "../../../Loader/Loader";

const LoyaltySettings = () => {
    const [token, setToken] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [settings, setSettings] = useState(null);

    useEffect(() => {
        const token = JSON.parse(localStorage.getItem('token'))
        if (token) {
            setToken(token);
        } else {
            setToken(null);
        }
    }, []);

    const formik = useFormik({
        initialValues: {
            pointsPerDollar: 1,
            redemptionRate: 1000,
            minRedemptionPoints: 500,
            rewardValuePerRate: 5
        },
        validationSchema: Yup.object({
            pointsPerDollar: Yup.number()
                .min(0.1, 'Points per dollar must be at least 0.1')
                .max(100, 'Points per dollar cannot exceed 100')
                .required('Points per dollar is required'),
            redemptionRate: Yup.number()
                .min(1, 'Redemption rate must be at least 1')
                .max(10000, 'Redemption rate cannot exceed 10000')
                .required('Redemption rate is required'),
            minRedemptionPoints: Yup.number()
                .min(1, 'Minimum redemption points must be at least 1')
                .max(5000, 'Minimum redemption points cannot exceed 5000')
                .required('Minimum redemption points is required'),
            rewardValuePerRate: Yup.number()
                .min(0.1, 'Reward value must be at least 0.1')
                .max(100, 'Reward value cannot exceed 100')
                .required('Reward value is required'),
        }),
        onSubmit: async (values) => {
            setLoading(true);
            const token = await JSON.parse(localStorage.getItem("token"));

            try {
                const response = await axios.put(`${baseURL}/api/admin/add-loyalty`,
                    values,
                    {
                        headers: {
                            Authorization: `${token}`,
                            "Content-Type": "application/json",
                        }
                    });

                if (response?.data?.message) {
                    setLoading(false);
                    getSettings();
                    Swal.fire({
                        title: response?.data?.message,
                        icon: "success",
                        confirmButtonColor: "#d3178a",
                    });
                }
            } catch (error) {
                setLoading(false);
                Swal.fire({
                    title: error?.response?.data?.error || "Error updating settings",
                    icon: "error",
                    confirmButtonColor: "#d3178a",
                });
            }
        },
    });

    const getSettings = async () => {
        if (!token) return;
        
        try {
            setIsLoading(true);
            const response = await axios.get(`${baseURL}/api/admin/get-loyalty`, {
                headers: {
                    Authorization: `${token}`,
                }
            });

            if (response?.data) {
                const data = response.data;
                setSettings(data);
                
                // Update formik values
                formik.setFieldValue("pointsPerDollar", data.pointsPerDollar || 1);
                formik.setFieldValue("redemptionRate", data.redemptionRate || 1000);
                formik.setFieldValue("minRedemptionPoints", data.minRedemptionPoints || 500);
                formik.setFieldValue("rewardValuePerRate", data.rewardValuePerRate || 5);
                
                setIsLoading(false);
            }
        } catch (error) {
            console.log(error);
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (token) {
            getSettings();
        }
    }, [token]);

    const resetToDefaults = () => {
        Swal.fire({
            title: 'Reset to Default Values?',
            text: "This will reset all loyalty settings to their default values.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d3178a',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, reset!'
        }).then((result) => {
            if (result.isConfirmed) {
                formik.setFieldValue("pointsPerDollar", 1);
                formik.setFieldValue("redemptionRate", 1000);
                formik.setFieldValue("minRedemptionPoints", 500);
                formik.setFieldValue("rewardValuePerRate", 5);
            }
        });
    };

    if (isLoading) {
        return <Loader />;
    }

    return (
        <Fragment>
            <Container fluid={true} style={{ paddingTop: '30px' }}>
                <Row>
                    <Col sm="12">
                        <Card>
                            <CardBody style={{ padding: '15px' }}>
                                <Row xxl={12} className='pb-2'>
                                    <Col md={6} lg={6} xl={6} xxl={6}>
                                        <div className="d-flex align-items-center">
                                            <Settings size={24} className="me-2 text-primary" />
                                            <h5 className='mb-0 font-bold'>
                                                Loyalty Settings
                                            </h5>
                                        </div>
                                        <p className="text-muted small mb-0 mt-1">
                                            Configure your customer loyalty program parameters
                                        </p>
                                    </Col>
                                    <Col md={6} lg={6} xl={6} xxl={6}>
                                        <div className="d-flex justify-content-end">
                                            <Button 
                                                className='btn btn-outline-secondary me-2 text-white' 
                                                onClick={resetToDefaults}
                                            >
                                                Reset to Defaults
                                            </Button>
                                        </div>
                                    </Col>
                                </Row>
                            </CardBody>

                            <CardBody>
                                <Form className="theme-form" onSubmit={formik.handleSubmit}>
                                    <Row>
                                        <Col xl={6}>
                                            <Card className="border">
                                                <CardBody>
                                                    <h6 className="font-semibold text-primary mb-3">
                                                        Point Earning Settings
                                                    </h6>
                                                    <FormGroup>
                                                        <Label className="font-semibold text-base">
                                                            Points Per Dollar Spent <span className="text-danger">*</span>
                                                        </Label>
                                                        <Input
                                                            type="number"
                                                            step="0.1"
                                                            min="0.1"
                                                            max="100"
                                                            name="pointsPerDollar"
                                                            placeholder="Enter points per dollar"
                                                            onChange={formik.handleChange}
                                                            onBlur={formik.handleBlur}
                                                            value={formik.values.pointsPerDollar}
                                                        />
                                                        <small className="text-muted">
                                                            How many points customers earn per $1 spent
                                                        </small>
                                                        {formik.touched.pointsPerDollar && formik.errors.pointsPerDollar ? (
                                                            <span className="error text-danger d-block">{formik.errors.pointsPerDollar}</span>
                                                        ) : null}
                                                    </FormGroup>
                                                </CardBody>
                                            </Card>
                                        </Col>

                                     

                                        <Col xl={6}>
                                            <Card className="border">
                                                <CardBody>
                                                    <h6 className="font-semibold text-primary mb-3">
                                                        Minimum Requirements
                                                    </h6>
                                                    <FormGroup>
                                                        <Label className="font-semibold text-base">
                                                            Minimum Points for Redemption <span className="text-danger">*</span>
                                                        </Label>
                                                        <Input
                                                            type="number"
                                                            min="1"
                                                            max="5000"
                                                            name="minRedemptionPoints"
                                                            placeholder="Enter minimum points"
                                                            onChange={formik.handleChange}
                                                            onBlur={formik.handleBlur}
                                                            value={formik.values.minRedemptionPoints}
                                                        />
                                                        <small className="text-muted">
                                                            Minimum points required before customers can redeem
                                                        </small>
                                                        {formik.touched.minRedemptionPoints && formik.errors.minRedemptionPoints ? (
                                                            <span className="error text-danger d-block">{formik.errors.minRedemptionPoints}</span>
                                                        ) : null}
                                                    </FormGroup>
                                                </CardBody>
                                            </Card>
                                        </Col>

                                           <Col xl={6}>
                                            <Card className="border">
                                                <CardBody>
                                                    <h6 className="font-semibold text-primary mb-3">
                                                        Redemption Settings
                                                    </h6>
                                                    <FormGroup>
                                                        <Label className="font-semibold text-base">
                                                            Points for Redemption <span className="text-danger">*</span>
                                                        </Label>
                                                        <Input
                                                            type="number"
                                                            min="1"
                                                            max="10000"
                                                            name="redemptionRate"
                                                            placeholder="Enter redemption rate"
                                                            onChange={formik.handleChange}
                                                            onBlur={formik.handleBlur}
                                                            value={formik.values.redemptionRate}
                                                        />
                                                        <small className="text-muted">
                                                            Number of points needed for reward redemption
                                                        </small>
                                                        {formik.touched.redemptionRate && formik.errors.redemptionRate ? (
                                                            <span className="error text-danger d-block">{formik.errors.redemptionRate}</span>
                                                        ) : null}
                                                    </FormGroup>

                                                    <FormGroup>
                                                        <Label className="font-semibold text-base">
                                                            Reward Value ($) <span className="text-danger">*</span>
                                                        </Label>
                                                        <Input
                                                            type="number"
                                                            step="0.1"
                                                            min="0.1"
                                                            max="100"
                                                            name="rewardValuePerRate"
                                                            placeholder="Enter reward value"
                                                            onChange={formik.handleChange}
                                                            onBlur={formik.handleBlur}
                                                            value={formik.values.rewardValuePerRate}
                                                        />
                                                        <small className="text-muted">
                                                            Dollar value of reward for redemption rate points
                                                        </small>
                                                        {formik.touched.rewardValuePerRate && formik.errors.rewardValuePerRate ? (
                                                            <span className="error text-danger d-block">{formik.errors.rewardValuePerRate}</span>
                                                        ) : null}
                                                    </FormGroup>
                                                </CardBody>
                                            </Card>
                                        </Col>

                                        <Col xl={6}>
                                            <Card className="border bg-light">
                                                <CardBody>
                                                    <h6 className="font-semibold text-success mb-3">
                                                        Current Configuration Preview
                                                    </h6>
                                                    <div className="loyalty-preview text-black">
                                                        <p className="mb-2">
                                                            <strong>Earning:</strong> ${formik.values.pointsPerDollar ? (1/formik.values.pointsPerDollar).toFixed(2) : '1.00'} spent = 1 point
                                                        </p>
                                                        <p className="mb-2">
                                                            <strong>Redemption:</strong> {formik.values.redemptionRate || 1000} points = ${formik.values.rewardValuePerRate || 5}
                                                        </p>
                                                        <p className="mb-2">
                                                            <strong>Min. Redemption:</strong> {formik.values.minRedemptionPoints || 500} points
                                                        </p>
                                                        <hr />
                                                        <p className="text-success mb-0">
                                                            <strong>Example:</strong> $100 purchase = {(100 * (formik.values.pointsPerDollar || 1)).toFixed(0)} points
                                                        </p>
                                                    </div>
                                                </CardBody>
                                            </Card>
                                        </Col>

                                        <Col xl={12} className="d-flex justify-content-end pt-3">
                                            <Button 
                                                type="submit" 
                                                disabled={loading || !formik.isValid} 
                                                className="btn btn-primary px-4"
                                            >
                                                {loading ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                        Saving...
                                                    </>
                                                ) : (
                                                    "Save Loyalty Settings"
                                                )}
                                            </Button>
                                        </Col>
                                    </Row>
                                </Form>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </Fragment>
    );
};

export default LoyaltySettings;